import PreFinanceiroEditor from './PreFinanceiroEditor';
import usePreviaParcelasMovimento from './usePreviaParcelasMovimento';
import React, { useMemo } from 'react';
import moment from 'moment';
import { ThemeProvider, Box, Tooltip, IconButton, TextField, Button, Alert } from '@mui/material';
import { Delete } from '@mui/icons-material';
import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';

import Input from '../../../../../form/Input';
import Checkbox from '../../../../../form/Checkbox';
import Select from '../../../../../form/Select';
import InputFloat from '../../../../../form/InputFloat';

import { maskDecimal, formatarNumero } from '../../../../../../utils/masks';

export default function LancamentoTabPanel({
    // UI
    theme,
    operacao,
    operacaoParametros,
    preFinanceiroBloqueado,

    // dados/handlers
    dados,
    onChange,
    onFocus,
    onBlur,

    // options
    OptionsForma,
    OptionsPrazo,
    OptionsConta,

    // grid
    data,
    manual,
    mrt,

    // ações
    onCancelarRow, // (rowOriginal) => void

    // helpers (pra não acoplar)
    retornaData,

    // permissões
    podeCancelar,
}) {
    const previa = usePreviaParcelasMovimento({
        dados, operacao, parametros: operacaoParametros, bloqueado: !!preFinanceiroBloqueado,
    });
    const columns = useMemo(
        () => [
            {
                id: 'lancamento',
                header: `Lançamentos do ${operacao?.descricao ?? ''}`,
                columns: [
                    {
                        accessorKey: 'parcela',
                        header: 'Parcela',
                        muiTableBodyCellProps: ({ row }) => ({
                            align: 'center',
                            onClick: () => {
                                console.info(row.original);
                            },
                        }),
                    },
                    {
                        accessorKey: 'dataVencimento',
                        header: 'Vencimento',
                        filterVariant: 'date',
                        sortingFn: 'date',
                        Cell: ({ row }) => (
                            <span>
                                {row.original.dataVencimento ? moment.utc(row.original.dataVencimento).format('DD/MM/YYYY') : ''}
                            </span>
                        ),
                        muiTableBodyCellProps: { align: 'center' },
                    },
                    {
                        accessorKey: 'valor',
                        header: 'Valor',
                        muiTableBodyCellProps: { align: 'right' },
                        accessorFn: (row) =>
                            row.valor == null
                                ? ''
                                : 'R$ ' +
                                  parseFloat(row.valor).toLocaleString('pt-BR', {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                  }),
                    },
                    {
                        accessorKey: 'valorBaixado',
                        header: 'Baixado',
                        muiTableBodyCellProps: { align: 'right' },
                        accessorFn: (row) =>
                            row.valorBaixado == null
                                ? ''
                                : 'R$ ' +
                                  parseFloat(row.valorBaixado).toLocaleString('pt-BR', {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                  }),
                    },
                    {
                        accessorKey: 'valorAberto',
                        header: 'Aberto',
                        muiTableBodyCellProps: { align: 'right' },
                        accessorFn: (row) =>
                            row.valorAberto == null
                                ? ''
                                : 'R$ ' +
                                  parseFloat(row.valorAberto).toLocaleString('pt-BR', {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                  }),
                    },
                    { accessorKey: 'codBarraPIX', header: 'Código de Barras / Chave PIX' },
                    {
                        accessorKey: 'historico',
                        header: 'Observação',
                        size: 360,
                        enableEditing: false,
                        muiTableBodyCellProps: {
                            sx: {
                                whiteSpace: 'pre-wrap',
                                overflowWrap: 'anywhere',
                            },
                        },
                    },
                    { accessorKey: 'Conta.descricao', id: 'Conta.descricao', header: 'Conta' },
                    { accessorKey: 'pagamentoForma.descricao', id: 'pagamentoForma.descricao', header: 'Forma' },
                    { accessorKey: 'pagamentoPrazo.descricao', id: 'pagamentoPrazo.descricao', header: 'Prazo' },
                ],
            },
        ],
        [operacao?.descricao]
    );

    const contaPrevia = dados?.contaId
        ? (Array.isArray(OptionsConta) ? OptionsConta : []).find((conta) => String(conta.id) === String(dados.contaId))
            ?? (String(dados?.Conta?.id) === String(dados.contaId) ? dados.Conta : null)
        : null;

    // A prévia tem paginação local e não usa filtros nem ações dos títulos gravados.
    const propsPrevia = previa.visivel ? {
        columns: [{
            id: 'previaParcelas',
            header: 'Prévia de parcelas',
            columns: columns[0].columns,
        }],
        data: previa.parcelas.map((parcela) => ({
            ...parcela,
            Conta: contaPrevia ? { descricao: contaPrevia.descricao ?? '' } : null,
        })),
        rowCount: previa.parcelas.length,
        manualPagination: false,
        manualSorting: false,
        manualFiltering: false,
        enableEditing: false,
        enableRowActions: false,
        enableRowSelection: false,
        enableFilters: false,
        enableColumnFilterModes: false,
        enableGlobalFilterModes: false,
        onColumnFiltersChange: undefined,
        onColumnFilterFnsChange: undefined,
        onGlobalFilterChange: undefined,
        onPaginationChange: undefined,
        onSortingChange: undefined,
        renderRowActions: undefined,
        muiToolbarAlertBannerProps: undefined,
        initialState: { density: 'compact', pagination: { pageIndex: 0, pageSize: 10 }, sorting: [{ id: 'parcela', desc: false }] },
        state: { showProgressBars: previa.ocupado },
    } : {};

    return (
        <div style={{ margin: '1em' }}>
            {/* Linha 1 */}
            <div>
                <Select
                    name="formaId"
                    title={`Define a forma do ${operacao?.descricao ?? ''}`}
                    text="Forma"
                    inputWidth="200px"
                    options={OptionsForma}
                    handleOnChange={onChange}
                    handleOnFocus={onFocus}
                    handleOnBlur={onBlur}
                    value={dados?.formaId ?? ''}
                    isEnum={false}
                    notOptNull={false}
                />

                <Select
                    name="prazoId"
                    title={`Define o prazo do ${operacao?.descricao ?? ''}`}
                    text="Prazo"
                    inputWidth="200px"
                    options={OptionsPrazo}
                    handleOnChange={onChange}
                    handleOnFocus={onFocus}
                    handleOnBlur={onBlur}
                    value={dados?.prazoId ?? ''}
                    isEnum={false}
                    notOptNull={false}
                />

                <Select
                    name="contaId"
                    title="Define a conta para o recebimento do lançamento"
                    text="Conta"
                    inputWidth="200px"
                    options={OptionsConta}
                    handleOnChange={onChange}
                    handleOnFocus={onFocus}
                    handleOnBlur={onBlur}
                    value={dados?.contaId ?? ''}
                    isEnum={false}
                    notOptNull={false}
                />

                <Checkbox
                    title="fixa o dia do vencimento"
                    text="Fixa Vencimento"
                    name="fixaDiaVencimento"
                    handleOnChange={onChange}
                    value={dados?.fixaDiaVencimento}
                    checked={!!dados?.fixaDiaVencimento}
                />

                <Input
                    text="Data Vencimento"
                    type="date"
                    name="primeiraDataVencimento"
                    placeholder="Data"
                    title="Data de vencimento da primeira parcela"
                    handleOnChange={onChange}
                    handleOnFocus={onFocus}
                    handleOnBlur={onBlur}
                    value={(dados?.primeiraDataVencimento && retornaData(dados.primeiraDataVencimento)) || retornaData(moment())}
                />
            </div>

            {/* Linha 2 */}
            <div>
                <InputFloat
                    text="Desconto Percentual"
                    name="descontoFinanceiroPercentual"
                    type="text"
                    title="valor total do desconto percentual financeiro será rateado aos itens do pedido"
                    value={dados?.descontoFinanceiroPercentual ?? ''}
                    handleOnChange={onChange}
                    handleOnFocus={onFocus}
                    handleOnBlur={onBlur}
                    inputWidth="150px"
                    textAlign="right"
                />

                <Input
                    text="Desconto"
                    name="descontoFinanceiro"
                    type="text"
                    title="valor total do desconto financeiro será rateado aos itens do pedido"
                    value={dados?.descontoFinanceiro ? formatarNumero(dados.descontoFinanceiro) : ''}
                    handleOnChange={onChange}
                    handleOnFocus={onFocus}
                    handleOnBlur={onBlur}
                    inputWidth="150px"
                    textAlign="right"
                />

                <Input
                    text="Total"
                    name="totalValor"
                    type="text"
                    title="Valor total dos lançamentos gerados"
                    value={dados?.totalValor ? maskDecimal(dados.totalValor) : '0,00'}
                    readonly={true}
                    inputWidth="150px"
                    textAlign="right"
                />

                <Input
                    text="Número de Parcelas"
                    name="totalParcela"
                    type="text"
                    title="número de parcelas geradas"
                    value={dados?.preFinanceiro?.parcelas?.length ?? dados?.totalParcela ?? '1'}
                    readonly={true}
                    inputWidth="150px"
                    textAlign="center"
                />

                {!dados?.preFinanceiro && (
                    <Input
                        text="Valor Parcela"
                        name="valorParcela"
                        type="text"
                        title="Valor das parcelas geradas"
                        value={dados?.valorParcela ? maskDecimal(dados.valorParcela) : '0,00'}
                        readonly={true}
                        inputWidth="150px"
                        textAlign="right"
                    />
                )}
            </div>
            {operacao?.sigla === 'XML' && (
                <TextField
                    id="movimento-xml-observacao-lancamento"
                    name="obsLancamento"
                    label="Observação complementar do lançamento"
                    placeholder="Ex.: Compra destinada à manutenção do forno."
                    helperText="Opcional. Este texto será acrescentado ao histórico automático dos lançamentos financeiros."
                    title="Opcional. Este texto será acrescentado ao histórico automático dos lançamentos financeiros."
                    value={dados?.obsLancamento ?? ''}
                    onChange={onChange}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    multiline
                    minRows={3}
                    fullWidth
                    size="small"
                    sx={{ mt: 2, mb: 2 }}
                />
            )}
            <PreFinanceiroEditor
                disabled={preFinanceiroBloqueado}
                dados={dados}
                operacao={operacao}
                operacaoParametros={operacaoParametros}
                onChange={onChange}
            />

            {previa.disponivel && (
                <Box sx={{ mt: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                        <Button
                            type="button"
                            variant="outlined"
                            onClick={previa.simular}
                            disabled={!!preFinanceiroBloqueado || previa.ocupado}
                            title="Conferir as parcelas calculadas com o total e as condições exibidos acima. Não grava lançamentos."
                        >
                            {previa.ocupado ? 'Simulando…' : previa.visivel ? 'Simular novamente' : 'Simular parcelas'}
                        </Button>
                        {previa.visivel && dados?.id && (
                            <Button type="button" variant="text" onClick={previa.mostrarGravados}>
                                Mostrar lançamentos gravados
                            </Button>
                        )}
                    </Box>
                    {previa.erro && <Alert severity="warning" sx={{ mb: 1 }}>{previa.erro}</Alert>}
                    {previa.desatualizada && !previa.visivel && (
                        <Alert severity="info" sx={{ mb: 1 }}>
                            A prévia ficou desatualizada. Confira as condições e clique em Simular parcelas novamente.
                        </Alert>
                    )}
                    {previa.visivel && (
                        <Alert severity="info">
                            Prévia — ainda não salva. {previa.parcelas.length} parcela(s) · Total: R$ {previa.total.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            })}.
                            {' '}Esta simulação não altera lançamentos. A gravação segue as validações do movimento.
                        </Alert>
                    )}
                </Box>
            )}

            {/* GRID */}
            {(!dados?.preFinanceiro || dados?.id) && (
                <ThemeProvider theme={theme}>
                    <MaterialReactTable
                        key={previa.visivel ? 'previa-parcelas' : 'lancamentos-gravados'}
                        columns={columns}
                        data={data || []}
                        rowCount={mrt?.rowCount ?? data?.length ?? 0}
                        getRowId={(row) => row.uuid}
                        manualPagination={!!manual}
                        manualSorting={!!manual}
                        manualFiltering={!!manual}
                        enableColumnFilterModes
                        enableGlobalFilterModes
                        enableColumnOrdering
                        enableEditing
                        localization={MRT_Localization_PT_BR}
                        initialState={{ showColumnFilters: true, density: 'compact' }}
                        muiToolbarAlertBannerProps={mrt?.isError ? { color: 'error', children: 'Error loading data' } : undefined}
                        onColumnFiltersChange={mrt?.setColumnFilters}
                        onColumnFilterFnsChange={mrt?.setColumnFilterFnsMode}
                        onGlobalFilterChange={mrt?.setGlobalFilter}
                        onPaginationChange={mrt?.setPagination}
                        onSortingChange={mrt?.setSorting}
                        state={{
                            columnFilterFns: mrt?.columnFilterFns,
                            columnFilters: mrt?.columnFilters,
                            globalFilter: mrt?.globalFilter,
                            isLoading: mrt?.isLoading,
                            pagination: mrt?.pagination,
                            showAlertBanner: mrt?.isError,
                            showProgressBars: mrt?.isRefetching,
                            sorting: mrt?.sorting,
                        }}
                        positionExpandColumn="first"
                        positionActionsColumn="first"
                        positionToolbarAlertBanner="bottom"
                        renderRowActions={({ row }) => (
                            <Box sx={{ display: 'flex', gap: '1rem' }}>
                                <Tooltip arrow placement="right" title="Cancelar">
                                    <span>
                                        <IconButton
                                            color="error"
                                            onClick={() => onCancelarRow?.(row.original)}
                                            disabled={!podeCancelar}
                                        >
                                            <Delete />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        )}
                        {...propsPrevia}
                    />
                </ThemeProvider>
            )}
        </div>
    );
}
